/**
 * Render-safe monogram extractor for avatar fallbacks.
 *
 * The browser's `<Avatar>` slot 2 (the fallback) renders this string when
 * the user has no avatar image. The rule across the product is: **first
 * letter of the first word + first letter of the last word**, uppercased.
 * Single-word names fall back to a single letter; empty/whitespace input
 * returns the question-mark sentinel.
 *
 * Examples:
 *   getMonogram('Amritpal Singh')             → 'AS'
 *   getMonogram('Ravi Kumar Sharma')          → 'RS'   (first + last, mid skipped)
 *   getMonogram('Madonna')                    → 'M'
 *   getMonogram('  ')                         → '?'
 *   getMonogram(undefined)                    → '?'
 *   getMonogram('Amritpal', 'Singh')          → 'AS'   (firstName + lastName form)
 *   getMonogram('Amritpal', '')               → 'A'
 *   getMonogram(null, null)                   → '?'
 *
 * Overloaded form 1 — single string with one or more whitespace-separated
 * parts (typical for `recipient.name` or `employeeName`).
 *
 * Overloaded form 2 — firstName + lastName supplied separately (typical
 * for `recipient.firstName` + `recipient.lastName` after JWT/profile
 * fetch where the server already split them).
 */
export function getMonogram(fullName?: string | null): string;
export function getMonogram(
  firstName?: string | null,
  lastName?: string | null,
): string;
export function getMonogram(
  a?: string | null,
  b?: string | null,
): string {
  // Form 2 — caller passed two explicit name parts.
  if (b !== undefined) {
    const first = (a ?? '').trim();
    const last = (b ?? '').trim();
    if (!first && !last) return '?';
    if (!first) return last.charAt(0).toUpperCase();
    if (!last) return first.charAt(0).toUpperCase();
    return (first.charAt(0) + last.charAt(0)).toUpperCase();
  }

  // Form 1 — single full-name string.
  const parts = (a ?? '')
    .trim()
    .split(/\s+/u)
    .filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
  return (
    parts[0].charAt(0) + parts[parts.length - 1].charAt(0)
  ).toUpperCase();
}
